/**
 * Reserved routes that should not be available for usernames
 * These routes are used by the system and would cause conflicts
 */

export const RESERVED_ROUTES = [
  // Core app routes
  'api',
  'app',
  'auth',
  'admin',
  'dashboard',
  'login',
  'logout',
  'signup',
  'signin',
  'register',
  'profile',
  'settings',
  'account',
  'user',
  'users',
  'forgot-password',
  'reset-password',
  'verify-email',
  'verify-account',
  'verify-email-code',

  // Content routes
  'explore',
  'search',
  'browse',
  'items',
  'catalog',
  'collections',
  'models',
  'brands',
  'about',
  'help',
  'support',
  'contact',
  'terms',
  'privacy',
  'legal',
  'trending',
  'popular',
  'new',
  'featured',
  'latest',
  'top',
  'best',
  
  // System routes
  'static',
  'assets',
  'images',
  'uploads',
  'files',
  'media',
  'cdn',
  'cache',
  'temp',
  'tmp',
  
  // Common web routes
  'www',
  'mail',
  'email',
  'blog',
  'news',
  'shop',
  'store',
  'cart',
  'checkout',
  'payment',
  'billing',
  
  // Tech/admin routes
  'admin',
  'moderator',
  'staff',
  'team',
  'dev',
  'test',
  'beta',
  'staging',
  'demo',
  'example',
  'sample',
  
  // Social/community routes
  'community',
  'forum',
  'chat',
  'discussion',
  'group',
  'club',
  'team',
  'crew',
  'gang',
  'squad',
  
  // Common names that could be confusing
  'me',
  'my',
  'i',
  'you',
  'we',
  'us',
  'them',
  'they',
  'he',
  'she',
  'it',
  
  // Platform-specific
  'diecastshowcase',
  'diecast',
  'showcase',
  'dc',
  'dsc',
] as const;

/**
 * Minimum username length requirement
 */
export const MIN_USERNAME_LENGTH = 5;

/**
 * Maximum username length
 */
export const MAX_USERNAME_LENGTH = 20;

/**
 * Username regex pattern (letters, numbers, underscores only)
 */
export const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;

/**
 * Check if a username is reserved
 */
export const isReservedRoute = (username: string): boolean => {
  return RESERVED_ROUTES.includes(username.toLowerCase() as any);
};

/**
 * Validate username format and length
 */
export const validateUsername = (username: string): { isValid: boolean; error?: string } => {
  if (!username) {
    return { isValid: false, error: 'Username is required' };
  }
  
  if (username.length < MIN_USERNAME_LENGTH) {
    return { isValid: false, error: `Username must be at least ${MIN_USERNAME_LENGTH} characters` };
  }
  
  if (username.length > MAX_USERNAME_LENGTH) {
    return { isValid: false, error: `Username must be less than ${MAX_USERNAME_LENGTH} characters` };
  }
  
  if (!USERNAME_REGEX.test(username)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, and underscores' };
  }
  
  if (isReservedRoute(username)) {
    return { isValid: false, error: 'This username is reserved and not available' };
  }
  
  return { isValid: true };
}; 